import { createAsyncThunk, original } from '@reduxjs/toolkit'
import ajv from '~/lib/ajv'
import conform from '~/lib/conformSettings'
import {
	CONTEXTS,
	DC_MESSAGES,
	HEIGHT,
	IS_FIREFOX,
	PROGRESS_STAGE_ORDER,
	RECORDING_STATUS,
	SESSION_STATUS,
	WIDTH,
	getProgressStage,
} from '~/lib/constants'
import socket from '~/lib/socket'
import { mediaArrived } from '~/lib/ownMedia'
import { transformImage } from '~/store/api/transforms'
import { findSourcePreset } from '~/lib/sourcePresets'
import { getRegionOrder } from '~/lib/latency'
import { assetsLoaded, isAssetRef, resolveAssetUrlSync } from '~/lib/resolveAssetUrl'
import { navigateTo } from '~/lib/navigate'
import { generateName, withAttachment } from '~/lib/utils'
import { display_objects_array_schema } from '~/schemas/display_object_schema'
import api from '~/store/api'
import { selectActiveRecording, selectProjects, selectSessionId, selectSessionStatus } from '~/store/api/selectors'
import { selectSettings } from '~/store/settings/selectors'
import { selectIsAdmin } from '~/store/auth/selectors'
import { selectSelectedObjectIds } from '~/store/editor/selectors'
import { parseSvgToObjects } from '~/lib/svg/parseSvg'
import { addObjects, deleteObjects, groupObjects, setLiveProperty, setObjectMask, updateObject } from '~/store/live'
import initialLive from '~/store/live/initial'
import { selectObjects } from '~/store/live/selectors'
import { gatherSubtreeIds } from '~/store/live/utils'
import { registerAction } from '~/store/middleware/confirmActions'
import { addLiveProp, setCurrentPresetIndex } from '~/store/playback'
import { pinnedInDelete } from '~/store/playback/pinThunks'
import { selectPlaylist, selectPresets, touchesFadingPreset } from '~/store/playback/selectors'
import { loadFirstTemplatePreset, loadPreset } from '~/store/playback/thunks'
import { clearObjectSelection, setObjectSelection, setSelection } from '~/store/selection'
import { selectFocus, selectIds, selectObjectIds } from '~/store/selection/selectors'
import {
	closeError,
	pushError,
	requestConfirm,
	setDialog,
	setPending,
	setRename,
	setShareDialog,
	setTextures,
	setUIProperty,
	toggleUIProperty,
} from '~/store/ui'
import { evictTextures, getCachedTexture, primeTextures } from '~/comps/PixiStage/usePixiObjects'
import { fieldWarmJobs, warmFields } from '~/comps/PixiStage/fieldWarm'
import { warmShaderDraws } from '~/comps/PixiStage/pixiShaderMesh'
import { isShaderSource } from '~/lib/shaders'
import { fillSourcesOf } from '~/lib/sourceFill'
import transitionManager from '~/lib/transitionManager'
import { getPersistKey } from '../persistUserId'
import { saveClientMode } from '~/lib/clientMode'
import { setClientMode, setProjectId } from './'
import { selectIsActiveOutput, selectIsExamples, selectIsRemote, selectProjectId } from './selectors'

const MOBILE_PROJECT_NAME = '_mobile'

const logger = console.scope('app-thunks', true)

const validateObjects = ajv.compile(display_objects_array_schema)

/**
 * Delete selected objects thunk.
 * @returns {Promise<void>}
 */
export const deleteSelectedObjects = (dispatch, getState) => {
	const s = getState()
	const ids = selectSelectedObjectIds(s)
	if (!ids?.length) {
		logger.warn('deleteSelectedObjects', 'No selected objects to delete')
		return
	}
	dispatch(clearObjectSelection())
	dispatch(deleteObjects(ids))
}

/** Delete `ids` at once, unless `deleteConfirm` asks first. The panels' Delete key; `deleteAction` asks the same way. */
export const deleteObjectsAsking = ids => (dispatch, getState) => {
	if (!ids?.length) return
	const confirm = deleteConfirm(getState(), ids)
	if (confirm) dispatch(requestConfirm(confirm))
	else dispatch(deleteObjects(ids))
}

/** The confirm a delete always asks: a pinned object, which leaves every preset too. */
const deleteConfirm = (s, ids) => {
	const pinned = pinnedInDelete(s, ids)
	if (!pinned.ids.length) return null
	const n = pinned.presets
	const what = pinned.ids.length === 1 ? 'A pinned object' : `${pinned.ids.length} pinned objects`
	return {
		title: 'Delete Pinned',
		message: `${what} will also be removed from ${n} preset${n === 1 ? '' : 's'}. Unpin first to keep ${pinned.ids.length === 1 ? 'it' : 'them'} in presets.`,
		action: { type: 'deleteObjects', params: ids },
		destructive: true,
	}
}

/**
 * Add a new project thunk. Creates the project shell, then the default preset, then loads the project.
 * @param {string} name - The desired name of the project
 * @returns {Promise<void>}
 */
export const addProject =
	(name = '') =>
	async (dispatch, getState) => {
		const s = getState()
		const projects = selectProjects(s) || []
		const uniqueName = generateName(
			projects.map(p => p.name),
			'name',
			name || 'Untitled Project'
		)
		dispatch(setPending(true))
		try {
			// Step 1: Create the project shell and set ID if successful
			logger.info('addProject', `Initiating mutation with name: ${uniqueName}`)
			const projectResult = await dispatch(api.endpoints.addProject.initiate({ name: uniqueName })).unwrap()

			if (!projectResult || !projectResult.id) {
				logger.error('addProject', 'No project shell or ID returned.')
				throw new Error('addProject thunk: Failed to create project shell or get ID.')
			}
			const newProjectId = projectResult.id

			// Step 2: Create the default preset for the new project
			const defaultPresetName = 'Preset 1'
			const defaultPresetPayload = {
				projectId: newProjectId,
				name: defaultPresetName,
				settings: initialLive,
			}
			logger.info('addProject', `Initiating addPreset mutation for project ${newProjectId}...`)
			await dispatch(api.endpoints.addPreset.initiate(defaultPresetPayload)).unwrap()
			logger.info('addProject', 'Default preset created.')

			// Step 3: Load the new project
			logger.info('addProject', `Loading project ${newProjectId}...`)
			dispatch(setProjectId(newProjectId))
		} catch (error) {
			logger.error('addProject', 'Failed:', error)
		} finally {
			dispatch(setPending(false))
		}
	}

/**
 * Add a template thunk. Creates a template from the current project.
 * @returns {Promise<void>}
 */
export const saveAsTemplate = () => async (dispatch, getState) => {
	const s = getState()
	if (s.app.is_template) {
		logger.warn('addTemplate', 'Cannot add template from another template')
		return
	}
	navigateTo('/new-template')
}

// Session Thunks

/**
 * Start session thunk. Starts a session with the given type (either 'xl' or 'regular')
 * @returns {Promise<void>}
 */
export const startSession = () => async (dispatch, getState) => {
	const s = getState()
	const status = selectSessionStatus(s)
	if (status !== SESSION_STATUS.IDLE) {
		logger.warn('startSession', 'Session already started')
		return
	}
	const body = { launch: true, type: s.settings.session_type }
	const regions = getRegionOrder()
	if (regions?.length) body.regions = regions.map(name => ({ regionName: name }))
	dispatch(api.endpoints.startSession.initiate(body))
}

/**f
 * Stop session thunk.
 * @returns {Promise<void>}
 */
export const stopSession = () => async (dispatch, getState) => {
	logger.info('stopSession')
	const s = getState()
	const status = selectSessionStatus(s)
	if (status === SESSION_STATUS.LOADING || status === SESSION_STATUS.IDLE) {
		return
	}
	const sessionId = selectSessionId(s)
	dispatch(api.endpoints.stopSession.initiate(sessionId))
}

registerAction('stopSession', () => stopSession())

// A remote start is unattended: no mic means record silent, since nobody may be at the desktop to answer.
export const startRecording =
	({ unattended = false } = {}) =>
	async (dispatch, getState) => {
		const s = getState()
		const sessionId = selectSessionId(s)
		if (!sessionId || selectActiveRecording(s)) return false
		const { fps, rec_source } = selectSettings(s)
		const { ensureAudioAttached } = await import('~/lib/audio')
		if (!(await ensureAudioAttached(socket))) {
			logger.error('Microphone unavailable - recording would have no audio')
			if (!unattended) {
				dispatch(
					requestConfirm({
						title: 'No Microphone',
						message: 'The microphone is unavailable — this recording will have no audio. Record anyway?',
						action: { type: 'startRecording', params: { sessionId, fps, rec_source } },
						confirmType: 'confirm_record_no_audio',
					})
				)
				return false
			}
		}
		const result = await dispatch(api.endpoints.startRecording.initiate({ sessionId, fps, rec_source }))
		if (result.error) logger.error('Failed to start recording', result.error)
		return !result.error
	}

/** @returns {Promise<boolean>} false when nothing was requested or the request failed */
export const stopRecording = () => async (dispatch, getState) => {
	const s = getState()
	const recording = selectActiveRecording(s)
	if (!recording) return false
	const result = await dispatch(
		api.endpoints.stopRecording.initiate({ sessionId: selectSessionId(s), recordingId: recording.id })
	)
	if (result.error) logger.error('Failed to stop recording', result.error)
	return !result.error
}

const imageSourcesIn = settings => {
	const out = []
	for (const obj of Object.values(settings?.objects ?? {})) {
		if (obj?.type === 'image' && obj.source) out.push(obj.source)
	}
	return out
}

/** Every image source the project can cue, across its playlist and whatever is live. */
const projectImageSources = state => {
	const sources = new Set(imageSourcesIn(state.live))
	for (const preset of selectPlaylist(state)) {
		for (const src of imageSourcesIn(preset.settings)) sources.add(src)
	}
	return sources
}

/** Every objects map the project can cue: its playlist's presets and whatever is live. */
const cueableScenes = state => [state.live?.objects, ...selectPlaylist(state).map(preset => preset.settings?.objects)]

// Fields warm beside the images, and never gate cueing: `textures.status` is the images' alone. A cue mid-warm bakes what it lacks synchronously. A later warm overtakes an earlier one (fieldWarm.js).
const warmSurfaceFields = state => {
	try {
		warmFields(fieldWarmJobs(cueableScenes(state)))
	} catch (e) {
		logger.warn('field warm failed', e)
	}
}

// ⚠️ A superseded warm writes nothing: an earlier one finishing inside a later one would open the gate on textures still loading, and its progress ticks would report a set no longer being fetched.
let warmGeneration = 0

// ⚠️ Never awaited by the UI — only cueing is gated, through `selectTexturesReady`.
export const warmTextures =
	({ evict: mayEvict = false } = {}) =>
	async (dispatch, getState) => {
		const generation = ++warmGeneration
		const current = () => generation === warmGeneration

		warmSurfaceFields(getState())
		warmShaderDraws(
			cueableScenes(getState()).flatMap(objects => [...fillSourcesOf(objects ?? {})].filter(isShaderSource))
		)

		const wanted = projectImageSources(getState())

		const unresolved = [...wanted].filter(src => isAssetRef(src) && !resolveAssetUrlSync(src))

		// ⚠️ A `selectPresets` listener fires this the moment `getProject` lands, often before `getAssets` — every ref then resolves to null, which is NOT READY, never a broken image. Stays `warming` so cueing keeps waiting for the warm that follows.
		if (unresolved.length && !assetsLoaded()) {
			if (current()) dispatch(setTextures({ status: 'warming', done: 0, total: wanted.size }))
			return
		}

		// ⚠️ The list HAS landed and these still do not resolve — the assets are gone, and waiting on them never ends. Holding `warming` here would disable every crossfade and autoplay for the rest of the session.
		for (const src of unresolved) wanted.delete(src)
		// Before the early returns below, so a project whose every remaining image is already resident still reports what it lost.
		if (unresolved.length && current()) {
			dispatch(
				pushError({
					code: 'ASSET_MISSING',
					title: 'Images',
					message: `${unresolved.length} image${unresolved.length > 1 ? 's' : ''} no longer exist and will not appear.`,
				})
			)
			dispatch(showErrorDialog())
		}
		// ⚠️ Only where the list has SETTLED: a preset mutation fires this listener per optimistic patch, and an intermediate set evicts a texture the next patch wants back.
		const evict = () => mayEvict && current() && !transitionManager.isLive() && evictTextures(wanted)

		const missing = [...wanted].filter(src => !getCachedTexture(src))
		if (!missing.length) {
			evict()
			if (current()) dispatch(setTextures({ status: 'ready', done: 0, total: 0 }))
			return
		}

		dispatch(setTextures({ status: 'warming', done: 0, total: missing.length }))
		const failed = await primeTextures(
			missing,
			(done, total) => current() && dispatch(setTextures({ done, total }))
		)
		if (!current()) return
		evict()
		dispatch(setTextures({ status: 'ready' }))

		if (failed.length) {
			dispatch(
				pushError({
					code: 'TEXTURE_PRELOAD',
					title: 'Images',
					message: `${failed.length} image${failed.length > 1 ? 's' : ''} could not be loaded and will not appear.`,
				})
			)
			dispatch(showErrorDialog())
		}
	}

/**
 * Upload asset and create image object
 * @param {string | File} fileOrUrl - The image file or URL to upload/use
 * @param {number} x - The x coordinate of the image
 * @param {number} y - The y coordinate of the image
 * @returns {Promise<void>}
 */
export const placeAsset =
	(source, x = 0, y = 0) =>
	dispatch => {
		logger.info('placeAsset', source)
		const displayUrl = resolveAssetUrlSync(source)
		if (!displayUrl) {
			logger.error('placeAsset', 'Could not resolve asset URL', source)
			return
		}

		const payload = { type: 'image', x, y, width: 400, height: 300, source }

		const img = new Image()
		img.onload = () => {
			let width = img.width
			let height = img.height
			if (width > WIDTH || height > HEIGHT) {
				const widthRatio = WIDTH / width
				const heightRatio = HEIGHT / height
				const ratio = Math.min(widthRatio, heightRatio)
				width = width * ratio
				height = height * ratio
			}

			payload.width = width
			payload.height = height

			dispatch(addObjects(payload))
			dispatch(warmTextures())
		}
		img.onerror = () => {
			logger.error('placeAsset', 'Image load error', source)
			dispatch(pushError({ code: 'PLACE_ASSET', title: 'Add Image', message: 'Failed to load image' }))
			dispatch(showErrorDialog())
		}

		if (!displayUrl.startsWith('blob:')) img.crossOrigin = 'use-credentials'
		img.src = displayUrl
	}

export const placeAssetFile = (file, x, y) => async (dispatch, getState) => {
	const blobUrl = URL.createObjectURL(file)
	const img = new Image()
	img.src = blobUrl
	await img.decode()

	let width = img.width,
		height = img.height
	if (width > WIDTH || height > HEIGHT) {
		const scale = Math.min(WIDTH / width, HEIGHT / height)
		width = Math.round(width * scale)
		height = Math.round(height * scale)
	}

	const beforeIds = new Set(Object.keys(getState().live.objects))
	dispatch(addObjects({ type: 'image', source: blobUrl, x, y, width, height, opacity: 50 }))
	const newId = Object.keys(getState().live.objects).find(id => !beforeIds.has(id))

	const formData = new FormData()
	formData.append('file', file)
	formData.append('metadata', JSON.stringify({ originalName: file.name, uploadedAt: new Date().toISOString() }))
	formData.append('thumbnail', 'true')

	try {
		const { data: asset } = await dispatch(api.endpoints.addAsset.initiate({ formData, file }))
		if (asset?.id) {
			dispatch(updateObject({ id: newId, source: `asset:${asset.id}`, opacity: 100 }))
			dispatch(warmTextures())
		}
	} catch {
		dispatch(deleteObjects([newId]))
	}
	URL.revokeObjectURL(blobUrl)
}

/**
 * Copy action thunk. Copies the selected objects or presets.
 * @returns {Promise<void>}
 */
export const copyAction = () => async (dispatch, getState) => {
	const s = getState()
	const focus = selectFocus(s)
	let type
	let data

	if (focus === 'preset') {
		const selectedPresetIds = selectIds(s)
		if (!selectedPresetIds.length) return
		const allPresets = selectPresets(s)
		type = CONTEXTS.PRESET
		data = allPresets.filter(p => selectedPresetIds.includes(p.id))
	} else if (focus === 'project') {
		const selectedProjectIds = selectIds(s)
		if (!selectedProjectIds.length) return
		const projects = api.endpoints.getProjects.select()(s).data
		type = CONTEXTS.PROJECT
		data = selectedProjectIds.map(id => projects.find(p => p.id === id)).filter(Boolean)
	} else {
		// Default to object/stage context
		const selectedIds = selectSelectedObjectIds(s)
		if (!selectedIds.length) return
		type = CONTEXTS.OBJECT

		/* Copy the whole subtree, not just the selected nodes. `children` holds ids, so a group copied alone pastes as a group whose children resolve to nothing. gatherSubtreeIds also dedupes, which covers selecting a group AND one of its own descendants. */
		const objects = selectObjects(s)
		const copiedIds = gatherSubtreeIds(objects, selectedIds)

		data = [...copiedIds].map(id => {
			const obj = { ...objects[id] }
			/* A mask partner outside the copied set would paste as a dangling ref, so drop the half we are carrying. recursivelyDeleteObjects does the equivalent cleanup on the delete side. */
			if (obj.mask && !copiedIds.has(obj.mask)) delete obj.mask
			if (obj.maskFor && !copiedIds.has(obj.maskFor)) delete obj.maskFor
			return obj
		})

		/* A tripwire: if it ever fires, cut must not fall through to the delete. */
		if (!data.length) throw new Error('copyAction: non-empty selection produced nothing to copy')
	}

	const text = JSON.stringify({
		type,
		data,
	})
	localStorage.setItem('clipboard', text)
	await navigator.clipboard.writeText(text)
	return true
}

/**
 * Delete action thunk. Deletes the selected objects or presets.
 * @returns {Promise<void>}
 */
export const deleteAction =
	(cut = false) =>
	async (dispatch, getState) => {
		const s = getState()
		const focus = selectFocus(s)
		if (!focus) return

		// Get the appropriate ids based on focus type
		let data
		if (focus === 'object') {
			data = selectObjectIds(s)
		} else {
			data = selectIds(s)
		}
		if (!data?.length) return

		let message
		logger.info(`deleteAction / ${focus}`, data)

		switch (focus) {
			case 'preset': {
				if (s.app.is_template) {
					navigateTo('/save_template_as')
					return
				}

				// ⚠️ Before the confirm dialog, not at the mutation: a preset the fade is interpolating cannot be renumbered out from under it, and by `onQueryStarted` the request is already away.
				if (touchesFadingPreset(s, data)) {
					logger.warn('deleteAction: refused, a selected preset is in the running fade')
					return
				}

				// Request confirmation before deleting presets
				if (data.length === 1) {
					const presets = selectPresets(s)
					const preset = presets.find(p => p.id === data[0])
					if (!preset) {
						logger.warn('deleteAction', `Preset id ${data[0]} not found.`)
						return
					}
					message = `Are you sure you want to delete "${preset.name}"?`
				} else {
					message = `Are you sure you want to delete ${data.length} presets?`
				}

				dispatch(
					requestConfirm({
						title: 'Delete Presets',
						message,
						action: { type: 'deletePresets', params: { projectId: s.app.project_id, ids: data } },
						confirmType: 'confirm_delete_preset',
					})
				)
				return
			}
			case 'object': {
				const confirm = deleteConfirm(s, data)
				if (confirm) {
					dispatch(requestConfirm(confirm))
					return
				}
				if (cut) {
					dispatch(deleteObjects(data))
				} else {
					dispatch(
						requestConfirm({
							title: 'Delete Objects',
							message: `Are you sure you want to delete ${data.length} objects?`,
							action: { type: 'deleteObjects', params: data },
							confirmType: 'confirm_delete_object',
						})
					)
				}
				return
			}
			case 'project': {
				const projects = selectProjects(s)

				// Single project deletion
				if (data.length === 1) {
					const project = projects.find(p => p.id === data[0])
					message = `Are you sure you want to delete "${project.name}"?`
					dispatch(
						requestConfirm({
							title: 'Delete Project',
							message,
							action: { type: 'deleteProject', params: data[0] },
							confirmType: 'confirm_delete_project',
						})
					)
				}
				// Multiple projects deletion
				else if (data.length > 1) {
					message = `Are you sure you want to delete ${data.length} projects?`
					dispatch(
						requestConfirm({
							title: 'Delete Projects',
							message,
							action: { type: 'deleteProjects', params: data },
							confirmType: 'confirm_delete_project',
						})
					)
				}
				return
			}
			case 'template': {
				if (data.length !== 1) {
					logger.warn('deleteAction', 'Can only delete one template at a time, wait for future')
					return
				}

				const templates = s.api.queries['getTemplates(undefined)']?.data || []
				const template = templates.find(p => p.id === data[0])
				message = `Are you sure you want to delete "${template.name}"?`

				// Request confirmation before deleting a template
				dispatch(
					requestConfirm({
						title: 'Delete Template',
						message,
						action: { type: 'deleteTemplate', params: data[0] },
					})
				)
				return
			}
			case 'recording': {
				const recordings = api.endpoints.getRecordings.select(undefined)(s)?.data || []

				if (data.length === 1) {
					const rec = recordings.find(r => r.id === data[0])
					if (!rec) return
					dispatch(
						requestConfirm({
							title: 'Delete Recording',
							message: `Are you sure you want to delete "${rec.name}"?`,
							action: {
								type: 'deleteRecording',
								params: { sessionId: rec.sessionId, recordingId: data[0] },
							},
						})
					)
				} else {
					const params = data
						.map(id => {
							const rec = recordings.find(r => r.id === id)
							return rec ? { sessionId: rec.sessionId, recordingId: id } : null
						})
						.filter(Boolean)
					if (!params.length) return
					dispatch(
						requestConfirm({
							title: 'Delete Recordings',
							message: `Are you sure you want to delete ${params.length} recordings?`,
							action: { type: 'deleteRecordings', params },
						})
					)
				}
				return
			}
			case 'asset': {
				const assets = api.endpoints.getAssets.select(undefined)(s)?.data || []
				const names = data.map(id => assets.find(a => a.id === id)?.name).filter(Boolean)
				if (names.length === 0) {
					logger.warn('deleteAction', 'No matching assets found')
					return
				}
				const message =
					data.length === 1
						? `Are you sure you want to delete "${names[0]}"?`
						: `Are you sure you want to delete ${data.length} assets?`
				dispatch(
					requestConfirm({
						title: data.length === 1 ? 'Delete Asset' : 'Delete Assets',
						message,
						action: { type: 'deleteAssets', params: data },
						confirmType: 'confirm_delete_asset',
					})
				)
				return
			}
			case 'image': {
				const images = api.endpoints.getImages.select(undefined)(s)?.data || []
				const names = data.map(id => images.find(i => i.id === id)?.name).filter(Boolean)
				if (!names.length) return
				dispatch(
					requestConfirm({
						title: names.length === 1 ? 'Delete Image' : 'Delete Images',
						message:
							names.length === 1
								? `Are you sure you want to delete "${names[0]}"?`
								: `Are you sure you want to delete ${names.length} images?`,
						action: { type: 'deleteImages', params: data },
					})
				)
				return
			}
			// Example presets are read-only. Without this the switch falls to `default` and logs an error every time Delete or Ctrl+X is pressed while one is selected.
			case CONTEXTS.EXAMPLE_PRESET:
				return
			default:
				logger.error('deleteAction', 'No valid focus type', focus)
		}
	}

/**
 * Cut action thunk. Copies the selected objects and deletes them.
 * @returns {Promise<void>}
 */
export const cutAction = () => async (dispatch, getState) => {
	const s = getState()
	const focus = selectFocus(s)

	if (focus === 'preset') {
		const selectedPresetIds = selectIds(s)
		if (!selectedPresetIds.length) return

		// A cut removes presets from the cache with no server call, leaving the paste to decide where they land — unresolved against a running playlist, so refused outright for now.
		if (s.playback.playing) {
			logger.warn('cutAction: refused, the playlist is playing')
			return
		}

		if (touchesFadingPreset(s, selectedPresetIds)) {
			logger.warn('cutAction: refused, a selected preset is in the running fade')
			return
		}

		const allPresets = selectPresets(s)
		const projectId = s.app.project_id

		// Serialize to clipboard with cut flag
		const data = allPresets.filter(p => selectedPresetIds.includes(p.id))
		const text = JSON.stringify({ type: CONTEXTS.PRESET, data, cut: true, projectId })
		localStorage.setItem('clipboard', text)
		await navigator.clipboard.writeText(text)

		// Optimistically remove from RTK cache (no server call)
		const idsSet = new Set(selectedPresetIds)
		const { current_preset_index } = s.playback
		const deletedIndices = allPresets.reduce((acc, p, i) => {
			if (idsSet.has(p.id)) acc.push(i)
			return acc
		}, [])
		const minDeletedIndex = Math.min(...deletedIndices)
		const maxDeletedIndex = minDeletedIndex + deletedIndices.length - 1

		dispatch(
			api.util.updateQueryData('getProject', projectId, d => {
				d.project.presets = d.project.presets.filter(p => !idsSet.has(p))
				d.presets = d.presets.filter(p => !idsSet.has(p.id))
			})
		)

		const remainingCount = allPresets.length - deletedIndices.length
		const remainingPresets = allPresets.filter(p => !idsSet.has(p.id))

		if (remainingCount === 0) {
			dispatch(setSelection({ focus: 'preset', ids: [] }))
			dispatch(loadFirstTemplatePreset())
		} else if (current_preset_index > maxDeletedIndex) {
			const newIndex = current_preset_index - deletedIndices.length
			dispatch(setCurrentPresetIndex(newIndex))
			dispatch(setSelection({ focus: 'preset', ids: [remainingPresets[newIndex]?.id].filter(Boolean) }))
		} else if (current_preset_index >= minDeletedIndex) {
			let newIndex
			if (maxDeletedIndex < allPresets.length - 1) {
				newIndex = minDeletedIndex
			} else {
				newIndex = Math.max(0, minDeletedIndex - 1)
			}
			dispatch(setSelection({ focus: 'preset', ids: [remainingPresets[newIndex]?.id].filter(Boolean) }))
			dispatch(loadPreset({ index: newIndex, confirmed: true, force: true, advance: true }))
		} else {
			dispatch(setSelection({ focus: 'preset', ids: [allPresets[current_preset_index]?.id].filter(Boolean) }))
		}
	} else {
		// Objects: copy then delete immediately
		/* Never delete what the clipboard did not take. copyAction returns falsy for an empty selection and throws if a non-empty one somehow yields nothing, so both paths stop short of the delete. */
		const copied = await dispatch(copyAction())
		if (!copied) return
		await dispatch(deleteAction(true))
	}
}

/**
 * Action thunk that handles pasting clipboard data. Validates and processes clipboard content which can be either preset data or object data. For presets, adds them to the current project with incremented order indices. For objects, adds them directly to the stage. Handles Firefox-specific clipboard access by checking localStorage first.
 *
 * @returns {Promise<void>}
 */
export const pasteAction =
	(providedText, { filename } = {}) =>
	async (dispatch, getState) => {
		let type,
			data,
			text = providedText ?? null
		if (!text) {
			if (IS_FIREFOX) {
				text = localStorage.getItem('clipboard')
			}
			if (!text) {
				try {
					text = await navigator.clipboard.readText()
				} catch {
					text = localStorage.getItem('clipboard')
				}
			}
		}
		if (!text) {
			try {
				const items = await navigator.clipboard.read()
				for (const item of items) {
					const imageType = item.types.find(t => t.startsWith('image/'))
					if (!imageType) continue
					const blob = await item.getType(imageType)

					if (imageType === 'image/svg+xml' && blob.size <= 100_000) {
						const svgText = await blob.text()
						if (parseSvgToObjects(svgText)) {
							dispatch(pasteAction(svgText))
							return
						}
					}
					const ext = imageType.split('/')[1].replace('svg+xml', 'svg')
					const file = new File([blob], `pasted.${ext}`, { type: imageType })
					dispatch(placeAssetFile(file, 0, 0))
					return
				}
			} catch {
				/* clipboard.read() unsupported or denied */
			}
			return
		}

		let parsed
		try {
			parsed = JSON.parse(text)
			type = parsed.type
			data = parsed.data
		} catch {
			// Not JSON — try SVG from external apps (Figma, Illustrator, etc.)
			const result = parseSvgToObjects(text)
			if (!result?.objects?.length) {
				if (/<svg[\s\S]*<\/svg>/i.test(text)) {
					const blob = new Blob([text], { type: 'image/svg+xml' })
					const file = new File([blob], 'pasted.svg', { type: 'image/svg+xml' })
					dispatch(placeAssetFile(file, 0, 0))
				}
				return
			}
			const { objects: svgObjects, masks } = result

			// Add all objects (tree structure via parentId)
			const beforeIds = new Set(Object.keys(getState().live.objects))
			dispatch(addObjects(svgObjects))
			const newIds = Object.keys(getState().live.objects).filter(id => !beforeIds.has(id))

			// Apply mask relationships
			if (masks?.length && newIds.length === svgObjects.length) {
				for (const { targetIndex, containerId } of masks) {
					const targetId = newIds[targetIndex]
					// Find the container's real ID after addObjects renamed it
					const containerIndex = svgObjects.findIndex(o => o.id === containerId)
					const maskId = containerIndex >= 0 ? newIds[containerIndex] : null
					if (!targetId || !maskId) continue
					dispatch(setObjectMask({ targetId, maskId }))
				}
			}

			// Group all pasted top-level objects under one wrap group
			const currentObjects = getState().live.objects
			const topLevelIds = Object.keys(currentObjects).filter(
				id => id !== 'root' && !beforeIds.has(id) && currentObjects[id].parentId === 'root'
			)
			if (topLevelIds.length >= 2) {
				dispatch(groupObjects(topLevelIds))
			}
			if (filename) {
				const name = filename.replace(/\.svg$/i, '').slice(0, 30)
				const afterObjects = getState().live.objects
				// Find the wrap group (created by groupObjects) or the single top-level group
				const targetId =
					topLevelIds.length >= 2
						? Object.keys(afterObjects).find(
								id => !beforeIds.has(id) && !currentObjects[id] && afterObjects[id]?.type === 'group'
							)
						: topLevelIds[0]
				if (targetId) dispatch(updateObject({ id: targetId, name }))
			}
			return
		}
		logger.info('pasteAction', type, data)
		if (!type || !data) {
			logger.warn('pasteAction', 'Invalid clipboard data', parsed)
			return
		}
		// A pasted PRESET is not checked here: `conform` runs inside every endpoint that can put one in the cache, so untrusted clipboard data hits the same door as everything else. Objects have no such funnel and are validated at the source.
		if (type !== CONTEXTS.PRESET) {
			const valid = validateObjects(data)
			if (!valid) {
				logger.error('pasteAction', 'Invalid clipboard data', { data, errors: validateObjects.errors })
				return
			}
		}

		// Get the current state once valid clipboard data is confirmed
		const s = getState()

		if (type === CONTEXTS.OBJECT) {
			dispatch(addObjects(data))
		} else if (type === CONTEXTS.PRESET) {
			if (s.app.is_template) {
				navigateTo('/save_template_as')
				return
			}
			const selectedPresetIds = selectIds(s)
			const allPresets = selectPresets(s)

			let insertAfter
			if (selectedPresetIds.length && selectFocus(s) === 'preset') {
				const selectedIndices = selectedPresetIds
					.map(id => allPresets.findIndex(p => p.id === id))
					.filter(i => i !== -1)
				insertAfter = Math.max(...selectedIndices)
			} else {
				insertAfter = s.playback.current_preset_index
			}

			const isCut = parsed.cut
			const sourceProjectId = parsed.projectId

			if (isCut && sourceProjectId === s.app.project_id) {
				// Same project cut+paste = reorder
				const cutIds = data.map(p => p.id)
				const currentOrder = [...allPresets.map(p => p.id)]
				// Re-add cut IDs (they were optimistically removed from cache) Insert them back: remove from current positions, insert at paste position
				const withoutCut = currentOrder.filter(id => !cutIds.includes(id))
				// insertAfter is relative to the current (post-cut) list
				const insertIndex = Math.min(insertAfter + 1, withoutCut.length)
				withoutCut.splice(insertIndex, 0, ...cutIds)

				// Re-add preset data to cache and update order
				dispatch(
					api.util.updateQueryData('getProject', s.app.project_id, d => {
						// Re-add preset objects if missing. ⚠️ This writes the cache directly rather than through an endpoint, so it is a door in its own right and conforms for itself — `data` is clipboard content.
						for (const preset of data) {
							if (!d.presets.find(p => p.id === preset.id)) {
								d.presets.push({ ...preset, settings: conform(preset.settings) })
							}
						}
						d.project.presets = withoutCut
					})
				)
				dispatch(api.endpoints.updateProject.initiate({ id: s.app.project_id, presets: withoutCut }))
			} else {
				// Different project (or regular copy+paste)
				const presets = data.map(({ name, settings, thumbnailKey, thumbnailUrl }, i) => {
					return { name, settings, thumbnailKey, thumbnailUrl, orderIndex: insertAfter + i + 1 }
				})
				const result = dispatch(api.endpoints.addPresets.initiate({ projectId: s.app.project_id, presets }))

				if (isCut && sourceProjectId) {
					// Delete from source project after successful paste
					result.unwrap().then(() => {
						const cutIds = data.map(p => p.id)
						dispatch(api.endpoints.deletePresets.initiate({ projectId: sourceProjectId, ids: cutIds }))
					})
				}
			}
		} else if (type === CONTEXTS.PROJECT) {
			for (const proj of data) {
				await dispatch(api.endpoints.cloneProject.initiate({ id: proj.id, name: `${proj.name} (copy)` }))
			}
		}
	}

/**
 * Start rename based on current selection focus. Reads focus + ids from the selection slice to determine what to rename. Only renames single-selected items.
 */
export const startRename = () => (dispatch, getState) => {
	const s = getState()
	const focus = selectFocus(s)
	if (!focus) return

	// Map selection focus to rename type (must match RenameField callbacks)
	const focusToRenameType = {
		object: 'layer',
		preset: 'preset',
		project: 'project',
		asset: 'asset',
		recording: 'recording',
		template: 'template',
		[CONTEXTS.SOURCE_PRESET]: 'source_preset',
	}
	const type = focusToRenameType[focus]
	if (!type) return

	// Only rename single selections
	const ids = focus === 'object' ? selectObjectIds(s) : selectIds(s)
	if (ids.length !== 1) return
	const id = ids[0]

	// Look up the item name and extra context from Redux state
	let name = ''
	let sessionId
	switch (focus) {
		case 'preset': {
			if (s.app.is_template) {
				navigateTo('/save_template_as')
				return
			}
			const presets = selectPresets(s)
			name = presets.find(p => p.id === id)?.name || ''
			break
		}
		case 'object':
			name = s.live.objects[id]?.name || ''
			break
		case 'project':
			name = selectProjects(s).find(p => p.id === id)?.name || ''
			break
		case 'asset':
			name = (api.endpoints.getAssets.select(undefined)(s)?.data || []).find(a => a.id === id)?.name || ''
			break
		case 'recording': {
			const rec = (api.endpoints.getRecordings.select(undefined)(s)?.data || []).find(r => r.id === id)
			name = rec?.name || ''
			sessionId = rec?.sessionId
			break
		}
		case 'template':
			name = (s.api.queries['getTemplates(undefined)']?.data || []).find(t => t.id === id)?.name || ''
			break
		case CONTEXTS.SOURCE_PRESET:
			name = findSourcePreset(s, id)?.preset.name || ''
			break
	}

	dispatch(
		setRename({
			id,
			projectId: selectProjectId(s),
			type,
			name,
			sessionId,
		})
	)
}

/**
 * Initializes the application by finding or creating a project and ensuring its data (including presets) is loaded.
 * @param {Object} options - Optional configuration
 * @param {boolean} options.forceNew - If true, always creates a new empty project (used by mobile)
 */
export const initProject = createAsyncThunk('app/initProject', async (options = {}, { dispatch }) => {
	const { forceNew = false } = options || {}
	const projectsPromise = dispatch(api.endpoints.getProjects.initiate())

	// If forceNew, always create a new project (for mobile)
	if (forceNew) {
		logger.info('initProject', 'Force new project requested')
		try {
			const res = await projectsPromise
			const projects = res.data || []
			const existingNames = projects.map(p => p.name)
			const defaultProjectName = generateName(existingNames, 'name', 'New Project')
			await dispatch(addProject(defaultProjectName))
			return null
		} catch (error) {
			logger.error('initProject', 'Failed to create new project:', error)
			throw error
		}
	}

	let projectId = localStorage.getItem(getPersistKey('project_id'))

	if (projectId) {
		logger.info('initProject', `Found localStorage ID ${projectId}`)
	}

	try {
		logger.info('initProject', 'Waiting for projects to load...')
		const res = await projectsPromise
		const projects = res.data

		if (!projects) {
			logger.error('initProject', 'No projects found, clearing localStorage ID')
			projectId = null
			localStorage.removeItem('project_id')
		} else if (projectId && !projects.find(p => p.id === projectId)) {
			logger.warn('initProject', 'No matching project found, clearing localStorage ID')
			projectId = null
			localStorage.removeItem('project_id')
		}

		// If no project ID in localStorage, select last edited project (excluding mobile-only project)
		if (!projectId && projects.length > 0) {
			logger.info('initProject', 'Selecting last edited project')
			const candidates = projects.filter(p => p.name !== MOBILE_PROJECT_NAME)
			if (candidates.length > 0) {
				const lastEditedProject = candidates.reduce((prev, current) => {
					const prevDate = new Date(prev.updatedAt)
					const currentDate = new Date(current.updatedAt)
					return prevDate > currentDate ? prev : current
				})
				projectId = lastEditedProject.id
			}
		}

		// If we have a project ID, set it in the app state to initiate loading
		if (projectId) {
			logger.info('initProject', `Using project ID ${projectId}`)
			dispatch(setProjectId(projectId))
			return projectId
		}

		// If still no project ID, delegate creation to addProject thunk
		logger.info('initProject', 'No projects found, creating new project')
		const existingNames = projects.map(p => p.name)
		const defaultProjectName = generateName(existingNames, 'name', 'Default Project')
		await dispatch(addProject(defaultProjectName)) // the thunk will take care of setting the project ID

		return projectId // Return the final determined project ID
	} catch (error) {
		logger.error('initProject', 'Failed:', error)
		projectsPromise.catch(err => logger.error('getProjects initiation failed:', err))
		throw error
	}
})

/**
 * Initialize the mobile-specific project. Mobile devices always use a project named '_mobile'. Template presets are loaded separately via getExamplePresets API.
 *
 * Priority:
 * 1. If '_mobile' project exists → use it
 * 2. Otherwise → create empty project (template presets loaded separately)
 */
/** The user's Camera | Remote choice, saved per device. The mobile app swaps on it: the outgoing app's unmount stops WebRTC and the incoming one reconnects with the new type in its offer. */
export const switchClientMode = mode => dispatch => {
	saveClientMode(mode)
	dispatch(setClientMode(mode))
}

export const initMobileProject = createAsyncThunk('app/initMobileProject', async (_, { dispatch }) => {
	logger.info('initMobileProject', 'Initializing mobile project...')

	try {
		// Fetch all projects
		const res = await dispatch(api.endpoints.getProjects.initiate()).unwrap()
		const projects = res || []

		// Look for existing _mobile project
		const mobileProject = projects.find(p => p.name === MOBILE_PROJECT_NAME)

		if (mobileProject) {
			logger.info('initMobileProject', `Found existing mobile project: ${mobileProject.id}`)
			dispatch(setProjectId(mobileProject.id))
			return mobileProject.id
		}

		// Create empty mobile project (template presets are loaded separately)
		logger.info('initMobileProject', 'Creating empty mobile project...')
		const projectResult = await dispatch(api.endpoints.addProject.initiate({ name: MOBILE_PROJECT_NAME })).unwrap()

		if (!projectResult?.id) {
			throw new Error('Failed to create mobile project')
		}

		const projectId = projectResult.id
		logger.info('initMobileProject', `Created empty mobile project: ${projectId}`)

		dispatch(setProjectId(projectId))
		return projectId
	} catch (error) {
		logger.error('initMobileProject', 'Failed:', error)
		throw error
	}
})

// The `getProject` subscription of the adopted project: held so `INVALIDATE_API_TAGS` refetches it, dropped on the next adoption.
let adopted = null

/**
 * A remote follows the session's project (C-project, R5): cache-first, the list only.
 *
 * ⚠️ R12: never `loadPreset` — the picture is the runner's, and a load here would write it.
 */
export const adoptSessionProject = (projectId, isTemplate) => async (dispatch, getState) => {
	const stillWanted = () => {
		const { playback } = getState()
		return (
			selectIsRemote(getState()) &&
			playback.project_id === projectId &&
			!!playback.project_template === !!isTemplate
		)
	}
	// The thumbs are CDN images behind signed cookies.
	import('~/lib/cdnAuth').then(m => m.refreshCdnCookies('remote'))
	if (isTemplate) {
		// A template is fetched as one and never cached or persisted as this phone's project.
		dispatch(setProjectId({ id: projectId, is_template: true }))
		adopted?.unsubscribe()
		adopted = dispatch(api.endpoints.getTemplate.initiate(projectId))
		return
	}
	const { getProjectCache, setProjectCache } = await import('~/routes/mobile/cache/presetThumbnailCache')
	// The cache only speeds the list up: a failed read falls through to the fetch.
	const cached = await getProjectCache(projectId).catch(err => logger.warn('adoptSessionProject cache', err))
	if (!stillWanted()) return
	if (cached?.data) await dispatch(api.util.upsertQueryData('getProject', projectId, cached.data))
	if (!stillWanted()) return
	dispatch(setProjectId(projectId))
	adopted?.unsubscribe()
	const sub = (adopted = dispatch(api.endpoints.getProject.initiate(projectId, { forceRefetch: true })))
	try {
		const fresh = await sub.unwrap()
		if (fresh?.project && fresh.project.updatedAt !== cached?.updatedAt)
			await setProjectCache(projectId, fresh, fresh.project.updatedAt)
	} catch (err) {
		logger.error('adoptSessionProject', projectId, err)
	}
}

/**
 * Download recording thunk
 */
export const openRecording = () => (dispatch, getState) => {
	const s = getState()
	const focus = selectFocus(s)
	const ids = selectIds(s)
	if (focus !== 'recording' || !ids.length) return

	const recordings = api.endpoints.getRecordings.select(undefined)(s)?.data || []
	const rec = recordings.find(r => ids.includes(r.id) && r.downloadUrl)
	if (rec) window.open(rec.downloadUrl, '_blank')
}

function downloadViaIframe(url) {
	return new Promise((resolve, reject) => {
		const iframe = document.createElement('iframe')
		iframe.style.display = 'none'
		iframe.src = url
		const cleanup = () => {
			if (iframe.parentNode) iframe.parentNode.removeChild(iframe)
		}
		iframe.addEventListener('load', () => {
			cleanup()
			reject(new Error('Download failed'))
		})
		document.body.appendChild(iframe)
		setTimeout(() => {
			cleanup()
			resolve()
		}, 5000)
	})
}

export const downloadRecording = () => async (dispatch, getState) => {
	const s = getState()
	const focus = selectFocus(s)
	const ids = selectIds(s)
	if (focus !== 'recording' || !ids.length) {
		logger.warn('downloadRecording', 'No recording selected')
		return
	}

	const recordings = api.endpoints.getRecordings.select(undefined)(s)?.data || []
	const toDownload = recordings.filter(r => ids.includes(r.id) && r.downloadUrl)

	if (!toDownload.length) {
		logger.warn('downloadRecording', 'No downloadable recordings found')
		return
	}

	logger.info('downloadRecording', `Downloading ${toDownload.length} recording(s)`)

	let failed = 0
	for (const recording of toDownload) {
		const filename = `${recording.name}.mp4`
		const url = withAttachment(recording.downloadUrl, filename)
		try {
			await downloadViaIframe(url)
		} catch {
			failed++
		}
		if (toDownload.length > 1) {
			await new Promise(resolve => setTimeout(resolve, 1000))
		}
	}
	if (failed) {
		dispatch(pushError({ title: 'Download', message: `${failed} download(s) failed` }))
		dispatch(showErrorDialog())
	}
}

export const shareRecording = () => (dispatch, getState) => {
	const s = getState()
	const focus = selectFocus(s)
	const ids = selectIds(s)
	if (focus !== 'recording' || ids.length !== 1) return

	const recordings = api.endpoints.getRecordings.select(undefined)(s)?.data || []
	const recording = recordings.find(r => r.id === ids[0])
	if (!recording || recording.status !== RECORDING_STATUS.MERGED) return

	dispatch(setShareDialog({ recordingId: recording.id, sessionId: recording.sessionId }))
}

export const downloadRecordingSource = () => async (dispatch, getState) => {
	const s = getState()
	const focus = selectFocus(s)
	const ids = selectIds(s)
	if (focus !== 'recording' || !ids.length) {
		logger.warn('downloadRecordingSource', 'No recording selected')
		return
	}

	const recordings = api.endpoints.getRecordings.select(undefined)(s)?.data || []
	const toDownload = recordings.filter(r => ids.includes(r.id) && r.sourceDownloadUrl)

	if (!toDownload.length) {
		logger.warn('downloadRecordingSource', 'No downloadable source recordings found')
		return
	}

	logger.info('downloadRecordingSource', `Downloading ${toDownload.length} source recording(s)`)

	let failed = 0
	for (const recording of toDownload) {
		const filename = `${recording.name}_source.mp4`
		const url = withAttachment(recording.sourceDownloadUrl, filename)
		try {
			await downloadViaIframe(url)
		} catch {
			failed++
		}
		if (toDownload.length > 1) {
			await new Promise(resolve => setTimeout(resolve, 1000))
		}
	}
	if (failed) {
		dispatch(pushError({ title: 'Download', message: `${failed} download(s) failed` }))
		dispatch(showErrorDialog())
	}
}

export const downloadAsset = () => async (dispatch, getState) => {
	const s = getState()
	const focus = selectFocus(s)
	const ids = selectIds(s)
	if (focus !== 'asset' || !ids.length) return

	const assets = api.endpoints.getAssets.select(undefined)(s)?.data || []
	const toDownload = assets.filter(a => ids.includes(a.id) && a.mediaUrl)

	let failed = 0
	for (const asset of toDownload) {
		const filename = asset.name
		const url = withAttachment(asset.mediaUrl, filename)
		try {
			await downloadViaIframe(url)
		} catch {
			failed++
		}
		if (toDownload.length > 1) {
			await new Promise(resolve => setTimeout(resolve, 1000))
		}
	}
	if (failed) {
		dispatch(pushError({ title: 'Download', message: `${failed} download(s) failed` }))
		dispatch(showErrorDialog())
	}
}

// orc's `image_status`: every client adds the image; `mediaArrived` tells the one that captured it.
export const imageCaptured = image => (dispatch, getState) => {
	const added = transformImage(image)
	if (api.endpoints.getImages.select(undefined)(getState())?.data) {
		dispatch(
			api.util.updateQueryData('getImages', undefined, d => {
				if (!d.some(i => i.id === added.id)) d.unshift(added)
			})
		)
	} else {
		dispatch(api.endpoints.getImages.initiate(undefined, { forceRefetch: true }))
	}
	mediaArrived('images', added.id)
}

export const downloadImage = () => async (dispatch, getState) => {
	const s = getState()
	const ids = selectIds(s)
	if (selectFocus(s) !== 'image' || !ids.length) return

	const images = api.endpoints.getImages.select(undefined)(s)?.data || []
	const toDownload = images.filter(i => ids.includes(i.id) && i.downloadUrl)

	let failed = 0
	for (const image of toDownload) {
		try {
			await downloadViaIframe(withAttachment(image.downloadUrl, `${image.name}.webp`))
		} catch {
			failed++
		}
		if (toDownload.length > 1) {
			await new Promise(resolve => setTimeout(resolve, 1000))
		}
	}
	if (failed) {
		dispatch(pushError({ title: 'Download', message: `${failed} download(s) failed` }))
		dispatch(showErrorDialog())
	}
}

// Use the focused asset as the IP-Adapter style reference. Stores "{assetId}:{name}" on ip.asset_id (NOT asset:{id}) — the realtime server builds the S3 key directly.
export const setAsImageStyle = () => (dispatch, getState) => {
	const s = getState()
	const focus = selectFocus(s)
	const ids = selectIds(s)
	if (focus !== 'asset' || !ids.length) return

	const assets = api.endpoints.getAssets.select(undefined)(s)?.data || []
	const asset = assets.find(a => ids.includes(a.id))
	if (!asset) return

	if (s.playback.next_preset_index >= 0) dispatch(addLiveProp('ip.asset_id'))
	dispatch(setLiveProperty(['ip.asset_id', `${asset.id}:${asset.name}`]))
}

export const updateSessionStatus = session => async dispatch => {
	await dispatch(
		api.util.updateQueryData('getActiveSession', undefined, draft => {
			if (typeof session === 'string') {
				session = JSON.parse(session)
			}

			// If status is 'stopped' or 'finished', transition to EMPTY_SESSION This prevents race condition where Ably update sets status to 'stopped', but subsequent getActiveSession refetch returns empty (since query filters don't include stopped/finished statuses), causing cache to revert to 'idle'
			if (session.status === 'stopped' || session.status === SESSION_STATUS.FINISHED) {
				logger.info('updateSessionStatus', `Session ${session.sessionId} ${session.status}, clearing session`)
				Object.assign(draft, {
					...draft,
					status: SESSION_STATUS.IDLE,
				})
				return
			}

			// Apply a (status, phase) update while keeping the derived PROGRESS STAGE monotonic. On GCP the launch subphases (machine_starting/app_starting/loading_models) ride under a single 'provisioning' status and arrive on an UNORDERED topic, so a stale/out-of-order phase can land after a later one. We re-stamp statusTimestamp only when the stage ADVANCES (each stage owns its own progress band and elapsed clock), and drop any event that would move the stage backward — else the progress bar rewinds. Phase is stored so useSessionProgress can key the band off the stage.
			const applyUpdate = () => {
				const prevStage = getProgressStage(draft.status, draft.phase)
				const nextStage = getProgressStage(session.status, session.phase)
				const prevIndex = PROGRESS_STAGE_ORDER.indexOf(prevStage)
				const nextIndex = PROGRESS_STAGE_ORDER.indexOf(nextStage)

				// Reject a backward stage move (both stages ordered, and next precedes prev).
				if (prevIndex !== -1 && nextIndex !== -1 && nextIndex < prevIndex) {
					logger.info('updateSessionStatus', `dropping backward stage ${prevStage} -> ${nextStage}`)
					return
				}

				draft.status = session.status
				draft.phase = session.phase || null
				// Stamp only on a real stage advance (or when either stage is off-axis, e.g. stop/finish paths).
				const stageAdvanced = nextIndex === -1 || prevIndex === -1 || nextIndex > prevIndex
				if (session.timestamp && stageAdvanced) draft.statusTimestamp = session.timestamp
			}

			if (draft.id?.length && draft.id === session.sessionId) {
				applyUpdate()
			} else if (draft.id?.length) {
				logger.warn('Received bad status update:', {
					data: session,
					local: original(draft),
				})
			} else {
				draft.id = session.sessionId
				draft.status = session.status
				draft.phase = session.phase || null
				if (session.timestamp) draft.statusTimestamp = session.timestamp
			}
		})
	)
}

// --- Error Thunks ---

/**
 * Show dialog for current error (errors[0])
 */
export const showErrorDialog = () => (dispatch, getState) => {
	const errors = getState().ui.errors
	if (errors.length === 0) return

	const error = errors[0]
	dispatch(
		setDialog({
			title: error.title || 'Error',
			message: error.message,
			okayLabel: 'OK',
			isError: true,
		})
	)
}

/**
 * Close current error and show next if any (called when dialog closes)
 */
export const showNextError = () => (dispatch, getState) => {
	dispatch(closeError())

	// If there are more errors, show the next one
	const errors = getState().ui.errors
	if (errors.length > 0) {
		dispatch(showErrorDialog())
	}
}

/**
 * Open the examples editing project for the given variant ('mobile' or 'desktop'). Always clones a fresh copy from the template (backend deletes any prior scratch project). Admin only.
 */
export const openExamplesProject = variant => async (dispatch, getState) => {
	if (!selectIsAdmin(getState())) {
		logger.error('openExamplesProject', 'Admin access required')
		return
	}

	logger.info('openExamplesProject', `Opening ${variant} examples project`)

	try {
		const project = await dispatch(api.endpoints.openExampleTemplate.initiate({ variant })).unwrap()

		dispatch(setProjectId({ id: project.id, is_examples: variant }))
	} catch (error) {
		logger.error('openExamplesProject', 'Failed to open examples project', error)
	}
}

/**
 * Publish the currently open examples project back to the template, then return to the last real project. Server deletes the scratch project after syncing.
 */
export const publishExamplesProject = () => async (dispatch, getState) => {
	const variant = selectIsExamples(getState())

	if (!variant) {
		logger.error('publishExamplesProject', 'Not currently editing an examples project')
		return
	}

	logger.info('publishExamplesProject', `Publishing ${variant} examples`)

	try {
		await dispatch(
			api.endpoints.saveExampleTemplate.initiate({
				variant,
				name: '_examples',
				manifest: { assets: [] },
			})
		).unwrap()

		logger.info('publishExamplesProject', 'Published successfully')

		const lastProjectId = localStorage.getItem(getPersistKey('project_id'))
		if (lastProjectId) {
			dispatch(setProjectId(lastProjectId))
		}
	} catch (error) {
		logger.error('publishExamplesProject', 'Failed to publish examples', error)
		dispatch(
			setDialog({
				title: 'Publish Failed',
				message: error?.data?.message || error?.message || 'Failed to publish examples',
				okayLabel: 'OK',
				isError: true,
			})
		)
	}
}

/**
 * Abort editing the examples project and return to the last real project. Stale scratch project is cleaned up on next open.
 */
export const abortExamplesProject = () => dispatch => {
	const lastProjectId = localStorage.getItem(getPersistKey('project_id'))
	if (!lastProjectId) {
		logger.error('abortExamplesProject', 'No last project ID found in local storage')
		return
	}

	dispatch(setProjectId(lastProjectId))
}

export const setPresetAsProjectThumb = payload => (dispatch, getState) => {
	const { id: presetId, projectId } = payload
	const state = getState()
	const projectData = state.api.queries[`getProject("${projectId}")`]?.data
	const preset = projectData?.presets?.find(p => p.id === presetId)
	const thumbKey = preset?.thumbnailKey
	if (!thumbKey) return

	const thumbUrl = preset?.thumbnailUrl || null

	// Optimistic: show the preset's own image immediately. The server then copies that object to a project-owned key and returns it, and setProjectThumbnail's onQueryStarted swaps these values for the real ones. We can't predict the copy's key here — it's versioned server-side.
	const patches = [
		dispatch(
			api.util.updateQueryData('getProject', projectId, d => {
				if (!d.project.settings) d.project.settings = {}
				d.project.settings.thumbnail = thumbKey
				d.project.thumbnailKey = thumbKey
				d.project.thumbnailUrl = thumbUrl
			})
		),
		dispatch(
			api.util.updateQueryData('getProjects', undefined, d => {
				const p = d.find(p => p.id === projectId)
				if (p) {
					if (!p.settings) p.settings = {}
					p.settings.thumbnail = thumbKey
					p.thumbnailKey = thumbKey
					p.thumbnailUrl = thumbUrl
				}
			})
		),
	]

	// NOT updateProject({ settings: { thumbnail: key } }) — storing the preset's key would dangle as soon as that preset is re-saved (preset thumbnails are versioned and the old object deleted). .unwrap() so a failed request actually rejects (a bare initiate() promise resolves with an `error` property instead) and the optimistic patches get reverted.
	dispatch(api.endpoints.setProjectThumbnail.initiate({ projectId, presetId }))
		.unwrap()
		.catch(() => {
			patches.reverse().forEach(p => p.undo())
		})
}

const onWire = state => selectSessionStatus(state) === SESSION_STATUS.RUNNING && state.app.dc_state === 'open'

// While running, the monitor shows what this window receives — and the mirror paints the same stream, so with it up only the monitor hides.
export const toggleOutput = () => (dispatch, getState) => {
	const state = getState()
	if (!onWire(state)) return dispatch(toggleUIProperty('show.output'))
	if (state.ui.mirror_open) return dispatch(setUIProperty(['monitor_hidden', !state.ui.monitor_hidden]))
	socket.sendDC(DC_MESSAGES.SEND_VIDEO, { send_video: !selectIsActiveOutput(state) })
}

/** The mirror came up: receive output for it, keeping a hidden monitor hidden. */
export const mirrorOpened = () => (dispatch, getState) => {
	const state = getState()
	dispatch(setUIProperty(['mirror_open', true]))
	if (!onWire(state) || selectIsActiveOutput(state)) return
	dispatch(setUIProperty(['monitor_hidden', true]))
	socket.sendDC(DC_MESSAGES.SEND_VIDEO, { send_video: true })
}

/** The mirror went away: a monitor it kept hidden stops receiving, as hiding it would have. */
export const mirrorClosed = () => (dispatch, getState) => {
	const hidden = getState().ui.monitor_hidden
	dispatch(setUIProperty(['mirror_open', false]))
	if (!hidden) return
	dispatch(setUIProperty(['monitor_hidden', false]))
	if (onWire(getState())) socket.sendDC(DC_MESSAGES.SEND_VIDEO, { send_video: false })
}
